import * as React from 'react';
import { Text, Title, Box, Group, Accordion, createStyles } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useStaticKey } from '../../hooks/useStaticKey';

// Contexts
import { ViewingAsContext } from '../../../../contexts/ViewingAs';

// i18n
import i18n from '../../../../i18n';

// Manifest
import { IFormSection, IManifestFieldHydrated } from '../../../../manifests/types';
import { manifest } from '../../../../manifests/questionManifest';

// Helpers
import { sectionsToConsolidatedSteps } from '../../helpers/sectionHelpers';

// Local Components
import { AnswerSummary } from './components/AnswerSummary';
import { AnswerValuePill } from './components/AnswerValuePill';
import { SummarySectionHeader } from './components/SummarySectionHeader';

// Types
import { ISurveyTargetPeriod } from '../../../../types';

const useStyles = createStyles((theme) => ({
    container: {
        // width: '100vw'
    },
    section: {
        // border: `1px solid #ced2d7`,
        // border: `1px solid ${theme.colors.brand[0]}`,
        border: `1px solid ${theme.colors.gray[3]}`,
        margin: 0
        // boxShadow: theme.shadows.xs
    },
    noBorder: {
        border: 'none'
    },
    sectionControl: {
        // color: '#546376',
        // background: '#f7f8f9',
        overflow: 'hidden',
        color: theme.colors.brand[6],
        background: theme.colors.brand[0],
        // color: theme.colors.blue[6],
        // background: theme.colors.blue[0],
        // color: theme.colors.orange[6],
        // background: theme.colors.orange[0],
        padding: `0 ${theme.spacing.md}`,
        borderRadius: 0
    },
    accordionContent: {
        padding: '0 !important'
    },
    accordionLabel: {
        paddingTop: '0.75rem',
        paddingBottom: '0.75rem'
    },
    accordionItem: {
        // borderColor: `${theme.colors.brand[0]} !important`
        // borderColor: `#ced2d7 !important`
    }
}));

interface IFormSummaryProps {
    sections?: IFormSection<IManifestFieldHydrated>[];
    answers: Record<string, any>;
    revisedSections?: number[];
    ancillaryData?: Record<string, any>;
    conditionalFields?: Record<string, boolean>;
    setActiveStep?: (val: number) => void;
    targetperiod?: ISurveyTargetPeriod;
    readOnly?: boolean;
    onHighlightQuestion?: (qid: string) => void;
    highlightedQuestions?: string[];
    flat?: boolean;
    noSectionHeaders?: boolean;
}

export const FormSummary: React.FC<IFormSummaryProps> = ({
    sections = [],
    answers,
    revisedSections = [0],
    ancillaryData,
    conditionalFields,
    setActiveStep,
    targetperiod,
    readOnly,
    onHighlightQuestion,
    highlightedQuestions,
    flat,
    noSectionHeaders
}) => {
    const { t } = useTranslation();
    const { classes, cx } = useStyles();
    const { getNameByStaticKey, getStaticKeyByName } = useStaticKey();
    const getAnswersByStaticKey = React.useCallback(
        (keys?: string[]) => {
            return (keys ?? []).reduce<Record<string, any>>((acc, curr) => {
                const valueName = getNameByStaticKey(curr);

                return {
                    ...acc,
                    [valueName]: answers?.[valueName]
                };
            }, {});
        },
        [answers]
    );
    const { userType } = React.useContext(ViewingAsContext);
    const stepsExtended = React.useMemo(() => sectionsToConsolidatedSteps(sections, t), [sections, i18n.language]);
    const stepsByQuestion = React.useMemo<Record<string, number>>(
        () =>
            (sections ?? []).reduce((acc, curr, index) => {
                return {
                    ...acc,
                    ...curr.manifest.reduce((acc2, { id, hidden }) => {
                        if (hidden === true || (Array.isArray(hidden) && userType && hidden.includes(userType))) {
                            return acc2;
                        } else {
                            return {
                                ...acc2,
                                [id]: index
                            };
                        }
                    }, {})
                };
            }, {}),
        [sections, userType]
    );

    return (
        <Accordion
            multiple
            variant={flat ? 'default' : 'separated'}
            className={classes.container}
            classNames={{
                content: classes.accordionContent,
                label: classes.accordionLabel,
                item: classes.accordionItem
            }}
            defaultValue={Array.from(Array(stepsExtended.length).keys())
                .filter((index) => !stepsExtended[index].collapsedOnReview)
                .map((val) => val.toString())}
        >
            {!readOnly ? (
                <>
                    <Title order={3}>{t('reviewAnswers.title', { ns: 'components' })}</Title>
                    <Text size="sm" mb="lg">
                        {t('reviewAnswers.body', { ns: 'components' })}
                    </Text>
                </>
            ) : null}
            {stepsExtended.map(({ label, skipReview, reviewSectionPillKey, questions = [] }, index) => {
                if (skipReview) return null;
                return (
                    <Accordion.Item
                        value={index.toString()}
                        className={cx(classes.section, {
                            [classes.noBorder]: flat
                        })}
                        key={index}
                    >
                        {!noSectionHeaders && !flat ? (
                            <Accordion.Control className={classes.sectionControl}>
                                <Group position="apart">
                                    <Text size="lg" fw={500}>
                                        {label}
                                    </Text>
                                    {reviewSectionPillKey ? (
                                        <AnswerValuePill answers={answers} staticKey={reviewSectionPillKey} />
                                    ) : null}
                                </Group>
                            </Accordion.Control>
                        ) : null}
                        {questions.map((val, questionIndex) => {
                            const fieldName = getNameByStaticKey(val);
                            const revisedConditionalFields = conditionalFields ?? {};
                            const hideField =
                                manifest?.[val]?.hideFromSummaryIfEmpty &&
                                (!answers?.[fieldName] ||
                                    (Array.isArray(answers?.[fieldName]) && !answers?.[fieldName].length));

                            return !hideField &&
                                (!Object.hasOwn(revisedConditionalFields, fieldName) ||
                                    revisedConditionalFields[fieldName] === true) &&
                                revisedSections.includes(stepsByQuestion[val]) ? (
                                <Accordion.Panel key={questionIndex}>
                                    <AnswerSummary
                                        noTopBorder={flat && questionIndex === 0}
                                        readOnly={readOnly}
                                        onHighlightQuestion={onHighlightQuestion}
                                        highlightedQuestions={highlightedQuestions}
                                        value={
                                            typeof manifest?.[val]?.answerStaticKeys !== 'undefined'
                                                ? getAnswersByStaticKey(manifest[val].answerStaticKeys)
                                                : answers?.[fieldName]
                                        }
                                        answerConfig={manifest?.[val]}
                                        ancillaryData={ancillaryData}
                                        targetperiod={targetperiod}
                                        onClick={() => {
                                            setActiveStep?.(stepsByQuestion[val]);
                                        }}
                                    />
                                </Accordion.Panel>
                            ) : null;
                        })}
                    </Accordion.Item>
                );
            })}
        </Accordion>
    );
};
